import { useState, useEffect, useCallback } from 'react';
import { fetchProductInstancesByExpression } from '~/service/productInstancesService';
import { fetchProjectById } from '~/service/projectsService';
import { formatMsrp } from '~/utils/formatters';
import { sortSeasons } from '~/utils/seasonSort';
import log from '~/logger';
function mapProjectToVariant(project) {
    return {
        id: project.id,
        handle: project.handle,
        name: project.projectName,
        subtitle: project.franchise?.label ?? '',
        msrp: formatMsrp(project.msrp),
        units: project.projectedUnit?.id,
        imagePath: '/assets/mockProductMain.png'
    };
}
function generateSeasonGenderGroupId(season, gender) {
    const normalizedSeason = season.toLowerCase().replace(/\s+/g, '');
    const normalizedGender = gender
        .toLowerCase()
        .replace(/\s+/g, '')
        .replace("'", '');
    return `${normalizedSeason}-${normalizedGender}`;
}
function groupProjectsBySeasonGender(projects, expressionContext) {
    const groups = new Map();
    const expressionSeasons = expressionContext.expressionSeasons ?? [];
    const expressionGender = expressionContext.expressionGender;
    const sortedSeasons = expressionSeasons.length > 0
        ? sortSeasons(expressionSeasons)
        : [];
    const seasonLabels = sortedSeasons.length > 0
        ? sortedSeasons.map((s) => s.label ?? 'Unknown')
        : ['Unknown'];
    const genderLabel = expressionGender?.label ?? 'Unisex';
    for (const seasonLabel of seasonLabels) {
        const groupId = generateSeasonGenderGroupId(seasonLabel, genderLabel);
        const groupName = `${seasonLabel} ${genderLabel}`;
        if (!groups.has(groupId)) {
            groups.set(groupId, {
                id: groupId,
                name: groupName,
                season: seasonLabel,
                gender: genderLabel,
                projects: []
            });
        }
    }
    for (const project of projects) {
        const variant = mapProjectToVariant(project);
        for (const seasonLabel of seasonLabels) {
            const groupId = generateSeasonGenderGroupId(seasonLabel, genderLabel);
            const group = groups.get(groupId);
            if (group) {
                group.projects.push(variant);
            }
        }
    }
    return Array.from(groups.values()).filter((group) => group.projects.length > 0);
}
/**
 * Fetches and groups projects linked to an expression via product instances.
 *
 * @param expressionId - The expression ID to fetch linked projects for
 * @param museDoors - The expression's museDoors (theme)
 * @param genderId - The expression's gender ID
 * @param expressionSeasons - The expression's seasons for grouping projects
 * @param expressionGender - The expression's gender for grouping projects
 * @returns Object containing projects grouped by expression season/gender, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { projects, isLoading, error, refetch } = useExpressionProjects(
 *   expression.id,
 *   expression.museDoors,
 *   expression.gender?.id,
 *   expression.seasons,
 *   expression.gender
 * )
 * ```
 */
export function useExpressionProjects(expressionId, museDoors, genderId, expressionSeasons, expressionGender) {
    const [projects, setProjects] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const loadProjects = useCallback(async () => {
        if (!expressionId) {
            setProjects([]);
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const productInstances = await fetchProductInstancesByExpression(expressionId);
            const uniqueProjectIds = [
                ...new Set(productInstances.map((pi) => pi.projectId))
            ];
            const projectResults = await Promise.all(uniqueProjectIds.map((id) => fetchProjectById(id)));
            const validProjects = projectResults.filter((p) => p !== null);
            const expressionContext = {
                museDoors,
                genderId,
                expressionSeasons,
                expressionGender
            };
            const grouped = groupProjectsBySeasonGender(validProjects, expressionContext);
            setProjects(grouped);
        }
        catch (err) {
            const errorObj = err instanceof Error ? err : new Error('Failed to load projects');
            setError(errorObj);
            log.error('Failed to load expression projects', {
                expressionId,
                error: errorObj.message
            });
        }
        finally {
            setIsLoading(false);
        }
    }, [expressionId, museDoors, genderId, expressionSeasons, expressionGender]);
    useEffect(() => {
        loadProjects();
    }, [loadProjects]);
    const refetch = useCallback(() => {
        loadProjects();
    }, [loadProjects]);
    return { projects, isLoading, error, refetch };
}
